import React, { HTMLAttributes, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';

import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import {
  Container,
} from '@uncinc/uncinc-react-kitchen-sink';

import { Button, Main } from '../../components';
import useClasses from '../../hooks/useClasses';

// Subcomponents
import Actions from './Actions';

import './style.scss';
import withModalContext, { useModalContext } from './Context';

interface Props extends React.DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  modalVisible: boolean;
  title?: string;
  closeModal?: () => void;
}

interface ModalSubcomponents {
  Actions: typeof Actions;
}

const ModalComponent: React.FC<Props> = ({
  className,
  modalVisible = false,
  title,
  children,
  closeModal,
}) => {
  const { t } = useTranslation();
  const { buildClass, joinClasses } = useClasses('Modal');

  const siteNamePretty = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['uiInternal', 'currentDomain', 'pretty'], ''));

  const documentTitle = useMemo(() => {
    // If both title and site name have not been provided, fall back to just Zelfhulp.
    if (!title && !siteNamePretty) {
      return 'Zelfhulp';
    }

    // If only a title has been provided, use that.
    if (title && !siteNamePretty) {
      return title;
    }

    // If only a site name has been provided, use that.
    if (!title && siteNamePretty) {
      return siteNamePretty;
    }

    // If both have been provided, create the requested title.
    return `${title} | ${siteNamePretty}`;
  }, [title, siteNamePretty]);

  const {
    headerRef,
    modalRef,
  } = useModalContext();

  if (!modalVisible) return null;

  return (
    <div className={joinClasses(buildClass(), className)} ref={modalRef}>
      <Helmet>
        <title>{documentTitle}</title>
      </Helmet>

      <div className={buildClass('header')} ref={headerRef}>
        <Container className={buildClass('header-container')}>
          <div />

          {title && (
            <h1 className={buildClass('title')}>
              {title}
            </h1>
          )}

          <div className={buildClass('close-button-container')}>
            {closeModal && (
              <Button
                iconOnly
                icon="cross"
                className={buildClass('close-button')}
                onClick={closeModal}
                aria-label={t('aria_labels.close_modal')}
              />
            )}
          </div>
        </Container>
      </div>
      <div className={buildClass('body')}>
        <Container className={buildClass('container')}>
          <Main className="row">
            {children}
          </Main>
        </Container>
      </div>
    </div>
  );
};

const Modal = withModalContext(ModalComponent) as typeof ModalComponent & ModalSubcomponents;

Modal.Actions = Actions;

export default Modal;
